import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_URL = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_URL,
});

// ── Browser cache busting ─────────────────────────────────────────────────────
// Ordered by specificity (longer paths first) so /pet-types matches before /pets
const RESOURCE_PATTERNS = [
  { pattern: '/pet-types',   key: 'pet-types'   },
  { pattern: '/visit-types', key: 'visit-types' },
  { pattern: '/owners',      key: 'owners'      },
  { pattern: '/pets',        key: 'pets'        },
  { pattern: '/doctors',     key: 'doctors'     },
  { pattern: '/services',    key: 'services'    },
  { pattern: '/schedules',   key: 'schedules'   },
  { pattern: '/visits',      key: 'visits'      },
  { pattern: '/dashboard',   key: 'dashboard'   },
  { pattern: '/subscription',key: 'subscription'},
];

// Resources whose mutations should also bust the dashboard cache
const DASHBOARD_AFFECTING = new Set([
  'owners', 'pets', 'pet-types', 'doctors', 'services', 'visit-types', 'visits', 'schedules',
]);

function getResourceKey(url) {
  if (!url) return null;
  for (const { pattern, key } of RESOURCE_PATTERNS) {
    if (url.includes(pattern)) return key;
  }
  return null;
}

function getResourceVersions() {
  try { return JSON.parse(localStorage.getItem('_rv') || '{}'); } catch { return {}; }
}

function bumpResourceVersion(resourceKey) {
  const versions = getResourceVersions();
  versions[resourceKey] = (parseInt(versions[resourceKey] || '0', 10) + 1).toString();
  if (DASHBOARD_AFFECTING.has(resourceKey)) {
    versions['dashboard'] = (parseInt(versions['dashboard'] || '0', 10) + 1).toString();
    versions['subscription'] = (parseInt(versions['subscription'] || '0', 10) + 1).toString();
  }
  localStorage.setItem('_rv', JSON.stringify(versions));
}

// ── Request interceptor ───────────────────────────────────────────────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // On GET requests append:
  //   _s  — per-login key  → busts cache when a different user logs in
  //   _rv — per-resource mutation counter → busts cache after add/edit/delete
  if (!config.method || config.method.toLowerCase() === 'get') {
    const sessionKey = localStorage.getItem('_session_key');
    const resourceKey = getResourceKey(config.url);
    const versions = getResourceVersions();
    if (sessionKey) {
      config.params = { ...config.params, _s: sessionKey };
    }
    if (resourceKey) {
      config.params = { ...config.params, _rv: versions[resourceKey] || '0' };
    }
  }
  return config;
});

// ── Response interceptor ──────────────────────────────────────────────────────
// After any successful mutation (POST / PUT / PATCH / DELETE), bump the version
// for that resource so the next GET gets a fresh URL → cache miss → real request.
api.interceptors.response.use((response) => {
  const method = response.config?.method?.toLowerCase();
  if (method && method !== 'get') {
    const resourceKey = getResourceKey(response.config.url);
    if (resourceKey) {
      bumpResourceVersion(resourceKey);
    }
  }
  return response;
});

export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('token', token);
    // New session key on every login → cross-user cache isolation
    localStorage.setItem('_session_key', Date.now().toString(36) + Math.random().toString(36).slice(2));
    // Reset per-resource versions so first requests in a new session are fresh
    localStorage.setItem('_rv', '{}');
  } else {
    localStorage.removeItem('token');
    localStorage.removeItem('_session_key');
    localStorage.removeItem('_rv');
  }
};

export const getUser = () => {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
};

export const setUser = (user) => {
  if (user) {
    localStorage.setItem('user', JSON.stringify(user));
  } else {
    localStorage.removeItem('user');
  }
};

export const logout = async () => {
  try {
    // Clear the server-side Redis cache for this clinic before leaving
    await api.post('/auth/logout');
  } catch (_) {
    // Ignore errors — proceed with local logout regardless
  }
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('_session_key');
  localStorage.removeItem('_rv');
  window.location.href = '/login';
};
